import { useCallback, useEffect, useMemo, useState } from "react";
import { disablePlan, editPlan, enablePlan, getPlans, newPlan } from "../../../services/plan-service";
import PlanModel from "../../../models/plan.model";

type ModalState = { open: boolean; plan?: PlanModel };

const PAGE_SIZE = 10;

/** Estado, fetch e handlers da administração de planos. */
export function useManagePlans() {
    const [loading, setLoading] = useState(false);
    const [plans, setPlans] = useState<PlanModel[]>([]);
    const [modal, setModal] = useState<ModalState>({ open: false });
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [periodFilter, setPeriodFilter] = useState<string>("all");

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        const result = await getPlans();
        setPlans(result.data as PlanModel[]);
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    const handleToggle = useCallback(
        async (plan: PlanModel) => {
            if (plan.enabled) {
                await disablePlan(plan.id);
            } else {
                await enablePlan(plan.id);
            }
            await fetchPlans();
        },
        [fetchPlans]
    );

    const handleSave = useCallback(
        async (plan: PlanModel, id?: number) => {
            if (id) {
                await editPlan(id, plan);
            } else {
                await newPlan(plan);
            }
            await fetchPlans();
            setModal({ open: false });
        },
        [fetchPlans]
    );

    const filteredPlans = useMemo(() => {
        return plans.filter((plan) => {
            const matchesSearch = !search || plan.name.toLowerCase().includes(search.toLowerCase());
            const matchesPeriod = periodFilter === "all" || String(plan.daysOfUse) === periodFilter;
            return matchesSearch && matchesPeriod;
        });
    }, [plans, search, periodFilter]);

    const pagedRows = filteredPlans.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const openCreate = () => setModal({ open: true });
    const openEdit = (plan: PlanModel) => setModal({ open: true, plan: { ...plan } });
    const closeModal = () => setModal({ open: false });

    const onSearch = (v: string) => {
        setSearch(v);
        setPage(1);
    };

    const onPeriodChange = (v: string) => {
        setPeriodFilter(v);
        setPage(1);
    };

    return {
        loading,
        plans,
        modal,
        page,
        setPage,
        search,
        periodFilter,
        pageSize: PAGE_SIZE,
        filteredPlans,
        pagedRows,
        handleToggle,
        handleSave,
        openCreate,
        openEdit,
        closeModal,
        onSearch,
        onPeriodChange,
    };
}
